import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStarterContext } from '@/context/StarterContext';
import { IndexedDBWizardRepository } from '@/lib/repositories/IndexedDBWizardRepository';
import { getWizardDay } from '@/lib/dateUtils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import type { WizardState } from '@/types/wizard';
import styles from '@/styles/components/WizardPage.module.css';

const wizardRepo = new IndexedDBWizardRepository();

function createInitialWizardState(): WizardState {
  const now = new Date().toISOString();
  return {
    active: true,
    startDate: now,
    currentDay: 1,
    dayProgress: [
      { day: 1, started: false, completed: false },
      { day: 4, started: false, completed: false },
    ],
  };
}

export function WizardPage() {
  const navigate = useNavigate();
  const { createStarter, recordFeeding } = useStarterContext();
  const [wizardState, setWizardState] = useState<WizardState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load or initialize wizard state
  useEffect(() => {
    async function load() {
      try {
        const existing = await wizardRepo.getWizardState();
        if (existing && existing.active) {
          setWizardState(existing);
        } else {
          const initial = createInitialWizardState();
          await wizardRepo.saveWizardState(initial);
          setWizardState(initial);
        }
      } catch {
        const initial = createInitialWizardState();
        setWizardState(initial);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const saveState = useCallback(async (state: WizardState) => {
    setWizardState(state);
    await wizardRepo.saveWizardState(state);
  }, []);

  const handleDay1Complete = useCallback(async () => {
    if (!wizardState) return;
    const updatedProgress = wizardState.dayProgress.map((dp) =>
      dp.day === 1
        ? { ...dp, started: true, completed: true, completedAt: new Date().toISOString() }
        : dp,
    );
    await saveState({ ...wizardState, dayProgress: updatedProgress });
  }, [wizardState, saveState]);

  const handleDay4Complete = useCallback(async () => {
    if (!wizardState) return;
    const updatedProgress = wizardState.dayProgress.map((dp) =>
      dp.day === 4
        ? { ...dp, started: true, completed: true, completedAt: new Date().toISOString() }
        : dp,
    );
    const completedState: WizardState = {
      ...wizardState,
      currentDay: 4,
      dayProgress: updatedProgress,
      completedAt: new Date().toISOString(),
      active: false,
    };
    await saveState(completedState);

    // Create the starter in the main repo
    try {
      await createStarter(wizardState.startDate, 'My Starter');
      await recordFeeding(new Date().toISOString(), 'First feeding (from wizard)');
    } catch {
      // Starter may already exist
    }
  }, [wizardState, saveState, createStarter, recordFeeding]);

  const handleCancel = useCallback(async () => {
    await wizardRepo.clearWizardState();
    navigate('/');
  }, [navigate]);

  const handleRestart = useCallback(async () => {
    const initial = createInitialWizardState();
    await saveState(initial);
  }, [saveState]);

  const handleGoToDashboard = useCallback(() => {
    navigate('/');
  }, [navigate]);

  if (isLoading) {
    return <div className={styles.page}>Loading wizard...</div>;
  }

  if (!wizardState) {
    return <div className={styles.page}>Error loading wizard state.</div>;
  }

  // Determine current display day based on time since wizard started
  const calendarDay = getWizardDay(wizardState.startDate);
  const day1Progress = wizardState.dayProgress.find((dp) => dp.day === 1);
  const day4Progress = wizardState.dayProgress.find((dp) => dp.day === 4);
  const day1Completed = day1Progress?.completed ?? false;
  const day4Completed = day4Progress?.completed ?? false;

  // Wizard completed
  if (day4Completed || wizardState.completedAt) {
    return (
      <div className={styles.page}>
        <div className={styles.completedState}>
          <div className={styles.completedIcon}>🎉</div>
          <h2 className={styles.completedTitle}>Your Starter is Active!</h2>
          <p className={styles.completedText}>
            You've completed the starter creation process. Your starter is now ready
            for regular feeding every 3-5 days.
          </p>
          <Button onClick={handleGoToDashboard}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  // Day 4: First feeding
  if (calendarDay >= 4 && day1Completed) {
    return (
      <div className={styles.page}>
        <ProgressStepper currentStep={3} />

        <h2 className={styles.dayTitle}>Day 4 — First Feeding</h2>
        <p className={styles.daySubtitle}>Time to wake up your starter!</p>

        <Card>
          <div className={styles.instructionsList}>
            <div className={styles.instructionItem}>
              <span className={styles.instructionNumber}>1</span>
              <span className={styles.instructionText}>
                Remove starter from refrigerator
              </span>
            </div>
            <div className={styles.instructionItem}>
              <span className={styles.instructionNumber}>2</span>
              <span className={styles.instructionText}>
                Add <strong>¾ cup sugar</strong>
              </span>
            </div>
            <div className={styles.instructionItem}>
              <span className={styles.instructionNumber}>3</span>
              <span className={styles.instructionText}>
                Add <strong>1 cup warm water</strong>
              </span>
            </div>
            <div className={styles.instructionItem}>
              <span className={styles.instructionNumber}>4</span>
              <span className={styles.instructionText}>
                Add <strong>3 Tbsp instant potato flakes</strong>
              </span>
            </div>
            <div className={styles.instructionItem}>
              <span className={styles.instructionNumber}>5</span>
              <span className={styles.instructionText}>
                Mix well, cover loosely, and let stand on counter 8-12 hours
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <div className={styles.guidanceBox}>
            <div className={styles.guidanceTitle}>💡 Is this normal?</div>
            <div className={styles.guidanceText}>
              Your starter may look flat and inactive after being in the fridge. That's
              perfectly normal! Once fed, it will become bubbly and active within 8-12
              hours. It should smell pleasantly sour, like yogurt.
            </div>
          </div>
        </Card>

        <div
          className={styles.checkboxRow}
          onClick={handleDay4Complete}
          role="checkbox"
          aria-checked={false}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleDay4Complete()}
        >
          <div className={styles.checkbox}>
            <span aria-hidden="true">&nbsp;</span>
          </div>
          <span className={styles.checkboxLabel}>I fed the starter</span>
        </div>

        <div className={styles.actionButtons}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel wizard
          </button>
        </div>
      </div>
    );
  }

  // Days 2-3: Waiting state
  if (calendarDay >= 2 && calendarDay <= 3 && day1Completed) {
    return (
      <div className={styles.page}>
        <ProgressStepper currentStep={2} />

        <div className={styles.waitingState}>
          <div className={styles.waitingIcon}>❄️</div>
          <h2 className={styles.waitingTitle}>Your Starter is Resting</h2>
          <p className={styles.waitingText}>
            Your starter is resting in the fridge. Come back on Day 4 to give it
            its first feeding!
          </p>
          <p className={styles.waitingText} style={{ marginTop: 'var(--spacing-sm)' }}>
            Day {calendarDay} of 4
          </p>
        </div>

        <Card>
          <div className={styles.guidanceBox}>
            <div className={styles.guidanceTitle}>💡 Is this normal?</div>
            <div className={styles.guidanceText}>
              Yes! The starter needs time to develop. The yeast is slowly activating
              in the cool environment of your fridge. There's nothing you need to do
              right now.
            </div>
          </div>
        </Card>

        <div className={styles.actionButtons}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel wizard
          </button>
        </div>
      </div>
    );
  }

  // Day 1: Mix ingredients (default)
  return (
    <div className={styles.page}>
      <ProgressStepper currentStep={1} />

      <h2 className={styles.dayTitle}>Day 1 — Create Your Starter</h2>
      <p className={styles.daySubtitle}>Mix the ingredients to begin your sourdough journey</p>

      <Card>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 'var(--spacing-md)' }}>
          Ingredients
        </h3>
        <div className={styles.instructionsList}>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>•</span>
            <span className={styles.instructionText}>
              1 envelope yeast (or 2 tsp / 7g active dry yeast)
            </span>
          </div>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>•</span>
            <span className={styles.instructionText}>
              1 cup warm water — <span className={styles.tempHighlight}>120°F</span>
            </span>
          </div>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>•</span>
            <span className={styles.instructionText}>¾ cup sugar</span>
          </div>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>•</span>
            <span className={styles.instructionText}>4 Tbsp instant potato flakes</span>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 'var(--spacing-md)' }}>
          Instructions
        </h3>
        <div className={styles.instructionsList}>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>1</span>
            <span className={styles.instructionText}>
              Mix all ingredients together in a container
            </span>
          </div>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>2</span>
            <span className={styles.instructionText}>
              Cover loosely and let stand at room temperature for 24 hours
            </span>
          </div>
          <div className={styles.instructionItem}>
            <span className={styles.instructionNumber}>3</span>
            <span className={styles.instructionText}>
              After 24 hours, put in the fridge for 3 days
            </span>
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.guidanceBox}>
          <div className={styles.guidanceTitle}>💡 Is this normal?</div>
          <div className={styles.guidanceText}>
            After mixing, the starter may look like thick pancake batter. Within 24
            hours at room temperature, you should see some bubbles forming on the
            surface. This is the yeast waking up — a great sign!
          </div>
        </div>
      </Card>

      {!day1Completed ? (
        <div
          className={styles.checkboxRow}
          onClick={handleDay1Complete}
          role="checkbox"
          aria-checked={false}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleDay1Complete()}
        >
          <div className={styles.checkbox}>
            <span aria-hidden="true">&nbsp;</span>
          </div>
          <span className={styles.checkboxLabel}>I mixed the starter</span>
        </div>
      ) : (
        <div className={styles.afterMessage}>
          ✓ Great! Cover loosely and let stand 24 hours at room temperature, then
          refrigerate. Come back on Day 4 to feed!
        </div>
      )}

      <div className={styles.actionButtons}>
        <button className={styles.cancelButton} onClick={handleRestart}>
          Restart wizard
        </button>
        <button className={styles.cancelButton} onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Visual stepper component
function ProgressStepper({ currentStep }: { currentStep: number }) {
  const steps = [
    { label: 'Day 1', step: 1 },
    { label: 'Days 2-3', step: 2 },
    { label: 'Day 4', step: 3 },
  ];

  return (
    <div>
      <div className={styles.stepper} role="group" aria-label="Wizard progress">
        {steps.map((s, i) => (
          <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <div
              className={`${styles.stepDot} ${
                currentStep === s.step
                  ? styles.stepDotActive
                  : currentStep > s.step
                    ? styles.stepDotDone
                    : ''
              }`}
              aria-label={`${s.label}: ${currentStep > s.step ? 'completed' : currentStep === s.step ? 'current' : 'upcoming'}`}
            />
            {i < steps.length - 1 && (
              <div
                className={`${styles.stepLine} ${currentStep > s.step ? styles.stepLineDone : ''}`}
              />
            )}
          </div>
        ))}
      </div>
      <p className={styles.stepLabel}>
        {steps.find((s) => s.step === currentStep)?.label ?? ''}
      </p>
    </div>
  );
}
